import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, ErrorState, PageHead, Skeleton, Stat, dateTime, hoursRemaining, money } from '@fa/ui';
import { api } from '../lib/api.js';
import type { ContingencyItem } from '../lib/types.js';
import { useDocumentDrawer } from '../DocumentDrawer.js';

/**
 * Fila de contingência.
 *
 * A coluna que importa e o prazo: a NFC-e emitida offline tem 24 horas para
 * ser transmitida. Passado o prazo, resolver deixa de ser automatico e vira
 * conversa com a SEFAZ — por isso o destaque visual comeca cedo.
 */
export function Contingency(): ReactNode {
  const { drawer, open } = useDocumentDrawer();

  const query = useQuery({
    queryKey: ['contingency'],
    queryFn: () => api<{ data: ContingencyItem[] }>('/v1/portal/contingency'),
    refetchInterval: 30_000,
  });

  const items = query.data?.data ?? [];
  const proximo = items.reduce<number | null>(
    (min, i) =>
      i.hours_remaining === null ? min : min === null ? i.hours_remaining : Math.min(min, i.hours_remaining),
    null,
  );
  const ufs = [...new Set(items.map((i) => i.uf))];

  return (
    <>
      <PageHead
        title="Contingência"
        subtitle="Notas assinadas e impressas enquanto a SEFAZ estava indisponível. Serão transmitidas sozinhas quando o serviço voltar."
      />

      <div className="cont-head">
        <Stat
          label="Aguardando transmissão"
          value={<span style={{ color: items.length > 0 ? 'var(--warn)' : undefined }}>{items.length}</span>}
          hint={ufs.length > 0 ? `SEFAZ ${ufs.join(', ')}` : 'nenhuma pendência'}
        />
        <Stat
          label="Prazo legal mais próximo"
          value={hoursRemaining(proximo)}
          hint={proximo !== null && proximo < 12 ? 'atenção: menos de 12 horas' : 'dentro do prazo'}
        />
        <Stat
          label="Verificação da SEFAZ"
          value="a cada 60s"
          hint="a transmissão começa sozinha quando o serviço voltar"
        />
      </div>

      <Card
        title="Fila de transmissão"
        padded={false}
        action={<span className="pill mute">ordem de numeração preservada</span>}
      >
        {query.isLoading && <Skeleton rows={4} />}
        {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}

        {query.data && (
          <table>
            <thead>
              <tr>
                <th>Nota</th>
                <th>Empresa</th>
                <th className="r">Valor</th>
                <th>Emitida em contingência</th>
                <th>Prazo restante</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="clickable" onClick={() => open(item.id)}>
                  <td>
                    <b className="num">{item.number.toLocaleString('pt-BR')}</b>
                    <span className="sub">Série {item.serie}</span>
                  </td>
                  <td>
                    {item.emitter}
                    <span className="sub">{item.uf}</span>
                  </td>
                  <td className="r num">{money(item.total)}</td>
                  <td className="num">{dateTime(item.created_at)}</td>
                  <td
                    className={`deadline num${item.hours_remaining !== null && item.hours_remaining < 12 ? ' warn' : ''}`}
                  >
                    {hoursRemaining(item.hours_remaining)}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 40 }}>
                    Nenhuma nota em contingência. Todas as SEFAZ dos seus emitentes estão respondendo.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      {drawer}
    </>
  );
}
